/**
 * useAudioRecorder Hook
 * Manages audio recording state and provides recording controls
 * Integrates browser permissions, MediaRecorder API, and state management
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  AudioRecorderError,
  AudioRecorderState,
  AudioRecordingData,
  MediaRecorderConfig,
  UseAudioRecorderReturn
} from '../models/audio-recorder.types';
import { AudioRecorderErrorType, PermissionState, RecordingState } from '../models/audio-recorder.types';
import { getBrowserCompatibility } from '../utils/audio-recorder-browser-compat';
import {
  canPauseRecording,
  cleanupMediaRecorder,
  createMediaRecorder,
  pauseMediaRecorder,
  resumeMediaRecorder,
  startMediaRecorder,
  stopMediaRecorder
} from '../utils/audio-recorder-media-recorder';
import { requestMicrophonePermission } from '../utils/audio-recorder-permissions';

/**
 * Custom hook for audio recording functionality
 * Provides complete recording lifecycle management
 */
export const useAudioRecorder = (config?: Partial<MediaRecorderConfig>): UseAudioRecorderReturn => {
  // Recording state
  const [state, setState] = useState<AudioRecorderState>({
    recordingState: RecordingState.IDLE,
    duration: 0,
    audioBlob: null,
    isPaused: false,
    error: null,
    isSupported: true,
    currentFormat: null
  });

  // Refs for MediaRecorder and MediaStream
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const startTimeRef = useRef<number>(0);
  const pausedTimeRef = useRef<number>(0);
  const timerRef = useRef<number | null>(null);
  const audioUrlRef = useRef<string | null>(null);

  /**
   * Update recording duration every 100ms
   */
  const startDurationTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current - pausedTimeRef.current;
      setState((prev) => ({
        ...prev,
        duration: Math.floor(elapsed / 1000) // Convert to seconds
      }));
    }, 100);
  }, []);

  /**
   * Stop duration timer
   */
  const stopDurationTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * Handle data available from MediaRecorder
   */
  const handleDataAvailable = useCallback((event: BlobEvent) => {
    if (event.data && event.data.size > 0) {
      audioChunksRef.current.push(event.data);
    }
  }, []);

  /**
   * Handle recording errors
   */
  const handleError = useCallback(
    (error: AudioRecorderError) => {
      setState((prev) => ({
        ...prev,
        recordingState: RecordingState.ERROR,
        error
      }));
      stopDurationTimer();
    },
    [stopDurationTimer]
  );

  /**
   * Start recording audio
   */
  const startRecording = useCallback(async (): Promise<void> => {
    try {
      // Check browser compatibility
      const compat = getBrowserCompatibility();
      if (!compat.isMediaRecorderSupported || !compat.isGetUserMediaSupported) {
        throw {
          type: AudioRecorderErrorType.NOT_SUPPORTED,
          message: 'Trình duyệt của bạn không hỗ trợ ghi âm',
          recoverable: false
        } as AudioRecorderError;
      }

      // Update state to requesting permission
      setState((prev) => ({
        ...prev,
        recordingState: RecordingState.REQUESTING,
        error: null
      }));

      // Request microphone permission
      const stream = await requestMicrophonePermission();
      mediaStreamRef.current = stream;

      // Create MediaRecorder
      const mediaRecorder = await createMediaRecorder(stream, config);
      mediaRecorderRef.current = mediaRecorder;

      // Reset audio chunks and timers
      audioChunksRef.current = [];
      startTimeRef.current = Date.now();
      pausedTimeRef.current = 0;

      // Update state to recording
      setState((prev) => ({
        ...prev,
        recordingState: RecordingState.RECORDING,
        duration: 0,
        audioBlob: null,
        isPaused: false
      }));

      // Clear previous audio URL if exists
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }

      // Start recording
      await startMediaRecorder(mediaRecorder, handleDataAvailable, handleError);

      // Start duration timer
      startDurationTimer();
    } catch (error) {
      const audioError = error as AudioRecorderError;
      handleError(audioError);

      // Cleanup on error
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }
    }
  }, [config, handleDataAvailable, handleError, startDurationTimer]);

  /**
   * Stop recording and create audio blob
   */
  const stopRecording = useCallback(async (): Promise<AudioRecordingData | null> => {
    try {
      if (!mediaRecorderRef.current || state.recordingState === RecordingState.IDLE) {
        return null;
      }

      // Stop duration timer
      stopDurationTimer();

      // Stop MediaRecorder
      await stopMediaRecorder(mediaRecorderRef.current);

      // Create audio blob from chunks
      const mimeType = mediaRecorderRef.current.mimeType || 'audio/webm';
      const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
      const audioUrl = URL.createObjectURL(audioBlob);
      audioUrlRef.current = audioUrl;

      // Calculate final duration
      const duration = Math.floor((Date.now() - startTimeRef.current - pausedTimeRef.current) / 1000);

      // Get format from browser compatibility
      const compat = getBrowserCompatibility();

      // Update state
      setState((prev) => ({
        ...prev,
        recordingState: RecordingState.STOPPED,
        audioBlob,
        duration,
        isPaused: false,
        currentFormat: compat.recommendedFormat
      }));

      // Cleanup media stream
      if (mediaStreamRef.current) {
        mediaStreamRef.current.getTracks().forEach((track) => track.stop());
        mediaStreamRef.current = null;
      }

      // Return recording data
      return {
        blob: audioBlob,
        duration,
        mimeType,
        size: audioBlob.size,
        format: compat.recommendedFormat || { mimeType, codec: 'opus', fileExtension: 'webm', quality: 'standard' }
      };
    } catch (error) {
      const audioError: AudioRecorderError = {
        type: AudioRecorderErrorType.RECORDING_FAILED,
        message: 'Không thể dừng ghi âm',
        originalError: error instanceof Error ? error : undefined,
        recoverable: true
      };
      handleError(audioError);
      return null;
    }
  }, [state.recordingState, stopDurationTimer, handleError]);

  /**
   * Pause recording (if supported)
   */
  const pauseRecording = useCallback((): void => {
    if (!mediaRecorderRef.current || state.recordingState !== RecordingState.RECORDING) {
      return;
    }

    if (!canPauseRecording()) {
      console.warn('Pause/resume not supported in this browser');
      return;
    }

    try {
      pauseMediaRecorder(mediaRecorderRef.current);
      const pauseStartTime = Date.now();

      setState((prev) => ({
        ...prev,
        recordingState: RecordingState.PAUSED,
        isPaused: true
      }));

      stopDurationTimer();

      // Store pause start time for duration calculation
      mediaRecorderRef.current.onresume = () => {
        pausedTimeRef.current += Date.now() - pauseStartTime;
        startDurationTimer();
      };
    } catch (error) {
      const audioError: AudioRecorderError = {
        type: AudioRecorderErrorType.RECORDING_FAILED,
        message: 'Không thể tạm dừng ghi âm',
        originalError: error instanceof Error ? error : undefined,
        recoverable: true
      };
      handleError(audioError);
    }
  }, [state.recordingState, stopDurationTimer, startDurationTimer, handleError]);

  /**
   * Resume recording (if supported)
   */
  const resumeRecording = useCallback((): void => {
    if (!mediaRecorderRef.current || state.recordingState !== RecordingState.PAUSED) {
      return;
    }

    if (!canPauseRecording()) {
      console.warn('Pause/resume not supported in this browser');
      return;
    }

    try {
      resumeMediaRecorder(mediaRecorderRef.current);

      setState((prev) => ({
        ...prev,
        recordingState: RecordingState.RECORDING,
        isPaused: false
      }));
    } catch (error) {
      const audioError: AudioRecorderError = {
        type: AudioRecorderErrorType.RECORDING_FAILED,
        message: 'Không thể tiếp tục ghi âm',
        originalError: error instanceof Error ? error : undefined,
        recoverable: true
      };
      handleError(audioError);
    }
  }, [state.recordingState, handleError]);

  /**
   * Cancel recording and cleanup
   */
  const cancelRecording = useCallback((): void => {
    stopDurationTimer();

    // Cleanup MediaRecorder and stream
    cleanupMediaRecorder(mediaRecorderRef.current, mediaStreamRef.current);
    mediaRecorderRef.current = null;
    mediaStreamRef.current = null;

    // Clear audio chunks
    audioChunksRef.current = [];

    // Revoke audio URL if exists
    if (audioUrlRef.current) {
      URL.revokeObjectURL(audioUrlRef.current);
      audioUrlRef.current = null;
    }

    // Reset state
    setState({
      recordingState: RecordingState.IDLE,
      duration: 0,
      audioBlob: null,
      isPaused: false,
      error: null,
      isSupported: true,
      currentFormat: null
    });
  }, [stopDurationTimer]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      stopDurationTimer();
      cleanupMediaRecorder(mediaRecorderRef.current, mediaStreamRef.current);

      // Revoke audio URL
      if (audioUrlRef.current) {
        URL.revokeObjectURL(audioUrlRef.current);
        audioUrlRef.current = null;
      }
    };
  }, [stopDurationTimer]);

  return {
    // State
    state,

    // Controls
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,

    // Utilities
    checkPermission: async () => {
      const status = await navigator.permissions?.query({ name: 'microphone' as PermissionName });
      return (status?.state as PermissionState) || PermissionState.UNKNOWN;
    },
    requestPermission: async () => {
      try {
        await requestMicrophonePermission();
        return true;
      } catch {
        return false;
      }
    },
    cleanup: () => {
      cancelRecording();
    }
  };
};
